import React from "react";

import {
  FiAlertTriangle,
  FiBell,
  FiExternalLink,
  FiInbox,
  FiMessageSquare,
} from "react-icons/fi";

import {
  SITE_PRODUCT_TONES,
} from "../utils/siteActivityConfig";

import {
  cn,
  formatMaybeNumber,
  formatNumber,
  getInitials,
} from "../utils/siteActivityFormatters";

import {
  ApiStatusBadge,
  ProductBadge,
} from "../ui/StatusBadge";

import SkeletonTable from "../ui/SkeletonTable";

/* ==========================================================================
   HEADER TRI
   ========================================================================== */

function SortTh({
  label,
  sortKey,
  activeKey,
  sortDir,
  onSort,
  align = "left",
}) {
  return (
    <th
      className={cn(
        "whitespace-nowrap px-4 py-4 text-xs font-bold uppercase tracking-[0.12em] text-slate-500",
        align === "right" &&
          "text-right",
      )}
    >
      <button
        type="button"
        onClick={() =>
          onSort(sortKey)
        }
        className="inline-flex items-center gap-1 transition hover:text-[#123B57] dark:hover:text-white"
      >
        {label}

        {activeKey ===
          sortKey && (
          <span className="text-[11px]">
            {sortDir ===
            "asc"
              ? "↑"
              : "↓"}
          </span>
        )}
      </button>
    </th>
  );
}

/* ==========================================================================
   LOGO
   ========================================================================== */

function Logo({
  src,
  name,
}) {
  if (!src) {
    return (
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#EEF7FC] text-sm font-bold text-[#123B57] dark:bg-slate-800 dark:text-white">
        {getInitials(
          name,
        )}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className="h-11 w-11 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
      onError={(
        event,
      ) => {
        event.currentTarget.style.display =
          "none";
      }}
    />
  );
}

/* ==========================================================================
   BOUTON COMPTEUR
   ========================================================================== */

function CountButton({
  icon,
  count,
  label,
  onClick,
  tone = "sky",
  stale = false,
}) {
  const hasValue =
    Number(count || 0) >
    0;

  const toneClasses = {
    sky:
      "bg-sky-500 text-white hover:bg-sky-600 dark:bg-sky-900/30 dark:text-sky-200",

    rose:
      "bg-rose-500 text-white hover:bg-rose-600 dark:bg-rose-900/30 dark:text-rose-200",

    amber:
      "bg-amber-500 text-white hover:bg-amber-600 dark:bg-amber-900/30 dark:text-amber-200",

    violet:
      "bg-violet-500 text-white hover:bg-violet-600 dark:bg-violet-900/30 dark:text-violet-200",
  };

  return (
    <button
      type="button"
      disabled={!hasValue}
      onClick={onClick}
      title={
        stale
          ? `${label} — données conservées depuis le dernier chargement réussi`
          : label
      }
      className={cn(
        "relative inline-flex min-w-[58px] items-center justify-center gap-1.5 rounded-xl px-2.5 py-2 text-xs font-bold transition",

        hasValue
          ? toneClasses[
              tone
            ] ||
              toneClasses.sky
          : "cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500",
      )}
    >
      {icon}

      {formatNumber(
        count || 0,
      )}

      {stale &&
        hasValue && (
          <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-400 dark:border-slate-900" />
        )}
    </button>
  );
}

/* ==========================================================================
   TITRE STATUT
   ========================================================================== */

function buildMonitorTitle(
  row,
) {
  const parts = [];

  if (
    row.criticalDataStale
  ) {
    parts.push(
      "Certaines alertes affichées proviennent du dernier chargement réussi.",
    );
  }

  if (
    row.criticalErrors
      ?.length
  ) {
    parts.push(
      `Alertes en erreur : ${row.criticalErrors
        .map(
          (item) =>
            item.key,
        )
        .join(", ")}`,
    );
  }

  if (
    row.metricErrors?.length
  ) {
    parts.push(
      `Statistiques indisponibles : ${row.metricErrors
        .map(
          (item) =>
            item.key,
        )
        .join(", ")}`,
    );
  }

  return (
    parts.join(" ") ||
    "Surveillance opérationnelle."
  );
}

/* ==========================================================================
   TABLEAU
   ========================================================================== */

export default function SitesActivityTable({
  rows,
  totals,
  loading,

  sortKey,
  sortDir,

  onSort,

  onOpenDetails,
}) {
  if (loading) {
    return (
      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <SkeletonTable
          rows={10}
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1380px]">
          <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur dark:bg-slate-800/95">
            <tr className="text-left">
              <SortTh
                label="Visites"
                sortKey="visitsToday"
                activeKey={
                  sortKey
                }
                sortDir={
                  sortDir
                }
                onSort={
                  onSort
                }
                align="right"
              />

              <th className="whitespace-nowrap px-4 py-4 text-center text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                Alertes
              </th>

              <SortTh
                label="Requêtes"
                sortKey="requestsPending"
                activeKey={
                  sortKey
                }
                sortDir={
                  sortDir
                }
                onSort={
                  onSort
                }
                align="right"
              />

              <SortTh
                label="Site"
                sortKey="name"
                activeKey={
                  sortKey
                }
                sortDir={
                  sortDir
                }
                onSort={
                  onSort
                }
              />

              <SortTh
                label="Produit"
                sortKey="productLabel"
                activeKey={
                  sortKey
                }
                sortDir={
                  sortDir
                }
                onSort={
                  onSort
                }
              />

              <SortTh
                label="Visites total"
                sortKey="visitsTotal"
                activeKey={
                  sortKey
                }
                sortDir={
                  sortDir
                }
                onSort={
                  onSort
                }
                align="right"
              />

              <SortTh
                label="Utilisateurs"
                sortKey="totalUsers"
                activeKey={
                  sortKey
                }
                sortDir={
                  sortDir
                }
                onSort={
                  onSort
                }
                align="right"
              />

              <SortTh
                label="Nouveaux"
                sortKey="todayUsers"
                activeKey={
                  sortKey
                }
                sortDir={
                  sortDir
                }
                onSort={
                  onSort
                }
                align="right"
              />

              <SortTh
                label="Adhérents"
                sortKey="adherents"
                activeKey={
                  sortKey
                }
                sortDir={
                  sortDir
                }
                onSort={
                  onSort
                }
                align="right"
              />

              <SortTh
                label="Inscrits"
                sortKey="inscrits"
                activeKey={
                  sortKey
                }
                sortDir={
                  sortDir
                }
                onSort={
                  onSort
                }
                align="right"
              />

              <th className="whitespace-nowrap px-4 py-4 text-center text-xs font-bold uppercase tracking-[0.12em] text-slate-500">
                Surveillance
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map(
              (
                row,
                index,
              ) => {
                const productTone =
                  SITE_PRODUCT_TONES[
                    row
                      .productKey
                  ] ||
                  "slate";

                const hasMetricError =
                  row.metricErrors
                    ?.length >
                  0;

                return (
                  <tr
                    key={
                      row.id ||
                      `${row.name}-${index}`
                    }
                    className={cn(
                      "border-t border-slate-100 transition dark:border-slate-800",

                      index % 2 ===
                        0
                        ? "bg-white dark:bg-slate-900"
                        : "bg-slate-50/40 dark:bg-slate-950/30",

                      "hover:bg-[#EEF7FC]/70 dark:hover:bg-slate-800/60",
                    )}
                  >
                    {/* VISITES DU JOUR */}
                    <td className="px-4 py-4 text-right font-bold tabular-nums text-[#123B57] dark:text-white">
                      {formatMaybeNumber(
                        row.visitsToday,
                      )}
                    </td>

                    {/* ALERTES */}
                    <td className="px-4 py-4">
                      <div className="flex justify-center gap-2">
                        <CountButton
                          icon={
                            <FiBell />
                          }
                          label="Notifications non lues"
                          count={
                            row
                              .notifications
                              ?.length ||
                            0
                          }
                          tone="amber"
                          stale={
                            row.criticalDataStale
                          }
                          onClick={() =>
                            onOpenDetails(
                              {
                                type:
                                  "notifications",

                                siteName:
                                  row.name,

                                items:
                                  row.notifications ||
                                  [],
                              },
                            )
                          }
                        />

                        <CountButton
                          icon={
                            <FiMessageSquare />
                          }
                          label="Messages non lus"
                          count={
                            row
                              .messages
                              ?.length ||
                            0
                          }
                          tone="sky"
                          stale={
                            row.criticalDataStale
                          }
                          onClick={() =>
                            onOpenDetails(
                              {
                                type:
                                  "messages",

                                siteName:
                                  row.name,

                                items:
                                  row.messages ||
                                  [],
                              },
                            )
                          }
                        />
                      </div>
                    </td>

                    {/* REQUÊTES */}
                    <td className="px-4 py-4 text-right">
                      <CountButton
                        icon={
                          <FiInbox />
                        }
                        label="Requêtes clients en attente"
                        count={
                          row.requestsPending ||
                          0
                        }
                        tone={
                          row.requestsPending >
                          0
                            ? "rose"
                            : "violet"
                        }
                        onClick={() =>
                          onOpenDetails(
                            {
                              type:
                                "requests",

                              siteName:
                                row.name,

                              items:
                                row.requests ||
                                [],
                            },
                          )
                        }
                      />
                    </td>

                    {/* SITE */}
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <Logo
                          src={
                            row.logo
                          }
                          name={
                            row.name
                          }
                        />

                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            {row.appUrl ? (
                              <a
                                href={
                                  row.appUrl
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block max-w-[280px] truncate text-sm font-bold text-[#123B57] hover:underline dark:text-white"
                                title={
                                  row.name
                                }
                              >
                                {
                                  row.name
                                }
                              </a>
                            ) : (
                              <span className="block max-w-[280px] truncate text-sm font-bold text-[#123B57] dark:text-white">
                                {
                                  row.name
                                }
                              </span>
                            )}

                            {row.appUrl && (
                              <FiExternalLink className="shrink-0 text-xs text-slate-400" />
                            )}
                          </div>

                          <div className="mt-1 max-w-[320px] truncate text-xs text-slate-400">
                            {row.appUrl ||
                              "URL non renseignée"}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* PRODUIT */}
                    <td className="px-4 py-4">
                      <ProductBadge
                        label={
                          row.productLabel
                        }
                        tone={
                          productTone
                        }
                      />
                    </td>

                    {/* VISITES TOTAL */}
                    <td className="px-4 py-4 text-right tabular-nums text-slate-600 dark:text-slate-300">
                      {formatMaybeNumber(
                        row.visitsTotal,
                      )}
                    </td>

                    {/* UTILISATEURS */}
                    <td className="px-4 py-4 text-right tabular-nums text-slate-600 dark:text-slate-300">
                      {formatMaybeNumber(
                        row.totalUsers,
                      )}
                    </td>

                    {/* NOUVEAUX */}
                    <td className="px-4 py-4 text-right">
                      {row.todayUsers ===
                        null ||
                      row.todayUsers ===
                        undefined ? (
                        <span className="text-sm text-slate-400">
                          —
                        </span>
                      ) : row.todayUsers >
                        0 ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                          +
                          {formatNumber(
                            row.todayUsers,
                          )}
                        </span>
                      ) : (
                        <span className="text-sm text-slate-400">
                          0
                        </span>
                      )}
                    </td>

                    {/* ADHÉRENTS */}
                    <td className="px-4 py-4 text-right tabular-nums text-slate-600 dark:text-slate-300">
                      {formatMaybeNumber(
                        row.adherents,
                      )}
                    </td>

                    {/* INSCRITS */}
                    <td className="px-4 py-4 text-right tabular-nums text-slate-600 dark:text-slate-300">
                      {formatMaybeNumber(
                        row.inscrits,
                      )}
                    </td>

                    {/* SURVEILLANCE */}
                    <td className="px-4 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <ApiStatusBadge
                          status={
                            row.monitorStatus
                          }
                          title={buildMonitorTitle(
                            row,
                          )}
                        />

                        {hasMetricError && (
                          <FiAlertTriangle
                            title={`Certaines statistiques sont indisponibles : ${row.metricErrors
                              .map(
                                (
                                  item,
                                ) =>
                                  item.key,
                              )
                              .join(
                                ", ",
                              )}`}
                            className="text-amber-500"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              },
            )}

            {!rows.length && (
              <tr>
                <td
                  colSpan={11}
                  className="px-4 py-12 text-center text-slate-500"
                >
                  Aucun site ne
                  correspond aux
                  filtres
                  sélectionnés.
                </td>
              </tr>
            )}
          </tbody>

          {!!rows.length && (
            <tfoot className="border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60">
              <tr className="text-sm font-bold text-[#123B57] dark:text-white">
                {/* Visites aujourd'hui */}
                <td className="px-4 py-4 text-right">
                  {formatNumber(
                    totals.visitsToday,
                  )}
                </td>

                {/* Alertes */}
                <td className="px-4 py-4 text-center">
                  {formatNumber(
                    totals.notifications +
                      totals.messages,
                  )}
                </td>

                {/* Requêtes */}
                <td className="px-4 py-4 text-right">
                  {formatNumber(
                    totals.requestsPending,
                  )}
                </td>

                {/* Site */}
                <td className="px-4 py-4">
                  {formatNumber(
                    totals.sites,
                  )}{" "}
                  site
                  {totals.sites >
                  1
                    ? "s"
                    : ""}
                </td>

                {/* Produit */}
                <td className="px-4 py-4">
                  —
                </td>

                {/* Visites total */}
                <td className="px-4 py-4 text-right">
                  {formatNumber(
                    totals.visitsTotal,
                  )}
                </td>

                {/* Utilisateurs */}
                <td className="px-4 py-4 text-right">
                  {formatNumber(
                    totals.totalUsers,
                  )}
                </td>

                {/* Nouveaux */}
                <td className="px-4 py-4 text-right">
                  +
                  {formatNumber(
                    totals.todayUsers,
                  )}
                </td>

                {/* Adhérents */}
                <td className="px-4 py-4 text-right">
                  {formatNumber(
                    totals.adherents,
                  )}
                </td>

                {/* Inscrits */}
                <td className="px-4 py-4 text-right">
                  {formatNumber(
                    totals.inscrits,
                  )}
                </td>

                {/* Surveillance */}
                <td className="px-4 py-4 text-center">
                  {totals.monitorWarnings >
                  0
                    ? `${totals.monitorWarnings} à vérifier`
                    : "OK"}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}